"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Decimal } from "decimal.js";
import { Info } from "lucide-react";
import { useCallback, useMemo, useRef, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { type Prisma } from "@/.zenstack/logical-prisma-client";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getAuctionBiddingDetails } from "@/lib/bids/bidding";
import { checkTenantScreeningCriteria } from "@/lib/bids/screening";
import { useSuspenseFindManyBid } from "@/lib/hooks/generated/tanstack-query";

import type { ListingForBidding, AuctionForBidding } from "./types";

// Create validation schema for bid form
const createBidFormSchema = (
  biddingDetails: ReturnType<typeof getAuctionBiddingDetails>,
  userId: string,
) =>
  z
    .object({
      currentBidAmount: z.coerce
        .number({
          message: "Please enter a valid bid amount",
        })
        .transform((val) => new Decimal(val)),
    })
    .refine(
      // Validation for non-winning users
      (data) => {
        if (!biddingDetails.isWinning(userId)) {
          return data.currentBidAmount.greaterThanOrEqualTo(
            biddingDetails.minimumNextBidAmount,
          );
        }
        return true;
      },
      {
        message: `Bid must be at least $${Number(biddingDetails.minimumNextBidAmount).toLocaleString("en-US")}`,
        path: ["currentBidAmount"],
      },
    );

type BidFormData = z.infer<ReturnType<typeof createBidFormSchema>>;

interface BidFormProps {
  listing: ListingForBidding;
  auction: AuctionForBidding;
  userId: string;
  propertySeeker?: Pick<
    Prisma.PropertySeekerGetPayload<{}>,
    "id" | "creditScoreRange" | "annualIncome" | "mostRecentEviction"
  > | null;
  onSuccess: () => void;
  onError: (error: string) => void;
}

export function BidForm({
  listing,
  auction,
  userId,
  propertySeeker,
  onSuccess,
  onError,
}: BidFormProps) {
  const queryClient = useQueryClient();
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  const { data: bids } = useSuspenseFindManyBid(
    {
      where: {
        auctionId: auction?.id,
      },
      select: {
        id: true,
        createdAt: true,
        createdById: true,
        currentBidAmount: true,
        placedBy: true,
        propertySeekerId: true,
        maxBidAmount: true,
      },
      orderBy: [
        {
          currentBidAmount: "desc",
        },
        {
          createdAt: "desc",
        },
      ],
    },
    {
      staleTime: 1000 * 10, // 10 seconds
    },
  );

  const biddingDetailsRef = useMemo(
    () => getAuctionBiddingDetails(auction, bids),
    [auction, bids],
  );

  const isWinning = biddingDetailsRef.isWinning(userId);

  const userHighestBidAmount = useMemo(() => {
    if (!bids || bids.length === 0) return null;
    const userBids = bids.filter((b) => b.createdById === userId);
    if (userBids.length === 0) return null;
    let maxAmount = 0;
    for (const b of userBids) {
      const candidate = Number(b.maxBidAmount ?? b.currentBidAmount);
      if (!Number.isNaN(candidate) && candidate > maxAmount) {
        maxAmount = candidate;
      }
    }
    return maxAmount || null;
  }, [bids, userId]);

  // Explicitly specify all generics to fix type inference issues with Zod v4 coercion
  // Type assertion needed due to Zod v4 .coerce.number() inference
  const form = useForm<BidFormData, any, BidFormData>({
    resolver: zodResolver(
      createBidFormSchema(biddingDetailsRef, userId),
    ) as any,
    defaultValues: {
      currentBidAmount: Number(biddingDetailsRef.minimumNextBidAmount),
    },
  });

  useEffect(() => {
    form.reset({
      currentBidAmount: Number(biddingDetailsRef.minimumNextBidAmount),
    });
  }, [biddingDetailsRef.minimumNextBidAmount, form]);

  const placeBid = useCallback(
    async (input: { auctionId: string; currentBidAmount: Decimal }) => {
      // Ensure PropertySeeker exists before placing bid
      if (
        listing.forLease?.tenantScreeningCriteria?.isEnabled &&
        !propertySeeker
      ) {
        toast("Please complete your tenant profile before placing a bid");
        return;
      }

      // Screening check (best effort from forLease)
      const passesScreening = checkTenantScreeningCriteria(
        listing.forLease?.tenantScreeningCriteria,
        propertySeeker,
      );

      if (!passesScreening.screeningPassed) {
        toast(passesScreening.reason.join("\n"));
        return;
      }

      // Submit to server route which enforces: cannot raise current bid if you're already winning
      await fetch("/api/bids/place", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          auctionId: input.auctionId,
          maxBidAmount: input.currentBidAmount.toString(),
        }),
      })
        .then(async (res) => {
          if (res.ok) {
            // This data is from the /api/bids/place route.
            const data = await res.json();
            if (!data.winning) {
              toast("You were outbid!");
            } else {
              toast("Bid placed successfully!");
            }
            // refetch the bids - invalidate all bid queries
            queryClient.refetchQueries({ queryKey: ["zenstack", "Bid"] });

            // Update biddingDetailsRef with fresh data
            // biddingDetailsRef.current = getAuctionBiddingDetails(auction, bids);
            onSuccessRef.current();
            // setFormSchema(
            //   createBidFormSchema(biddingDetailsRef.current, userId),
            // );
            // form.reset({
            //   currentBidAmount: biddingDetailsRef.current.minimumNextBidAmount,
            //   maxBidAmount: biddingDetailsRef.current.minimumNextMaxBidAmount,
            // });
          } else {
            const text = await res.text();
            const errorMessage = text || "Failed to place bid";
            toast(errorMessage); // refetch the bids - invalidate all bid queries

            // our local bid cache may be outdated
            //            queryClient.invalidateQueries({ queryKey: ["zenstack", "Bid"] });
            queryClient.refetchQueries({ queryKey: ["zenstack", "Bid"] });

            // Update biddingDetailsRef with fresh data
            //            biddingDetailsRef.current = getAuctionBiddingDetails(auction, bids);

            onErrorRef.current(errorMessage);
          }
        })
        .catch((error) => {
          const errorMessage = error.message || "Failed to place bid";
          toast(errorMessage);
          onErrorRef.current(errorMessage);
        });
    },
    [listing, propertySeeker, queryClient],
  );

  // Handle form submission
  const handleSubmit = async (data: BidFormData) => {
    form.clearErrors("root");
    try {
      await placeBid({
        auctionId: auction.id,
        currentBidAmount: data.currentBidAmount,
      });
    } catch (error: unknown) {
      // Surface server 400s directly on form-level error
      const message =
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof error.message === "string"
          ? error.message
          : "Failed to place bid";
      form.setError("root", { type: "server", message });
      onErrorRef.current(message);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="currentBidAmount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <span className="inline-flex items-center gap-1">
                  Max bid amount
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="max-w-xs text-center whitespace-normal"
                    >
                      Your max bid sets the highest amount you&apos;re willing
                      to pay. We&apos;ll automatically bid in preset increments
                      up to your max bid.
                    </TooltipContent>
                  </Tooltip>
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  min={
                    isWinning
                      ? Number(biddingDetailsRef.currentBidAmount)
                      : Number(biddingDetailsRef.minimumNextBidAmount)
                  }
                  step={Number(biddingDetailsRef.bidIncrement)}
                  value={Number(field.value ?? "")}
                />
              </FormControl>
              <FormDescription>
                {isWinning
                  ? `You're currently winning. ${
                      userHighestBidAmount
                        ? `Your max bid is $${userHighestBidAmount.toLocaleString("en-US")}.`
                        : "Raise your bid amount to protect your lead."
                    }`
                  : `Minimum bid amount is $${Number(biddingDetailsRef.minimumNextBidAmount).toLocaleString("en-US")}.`}
              </FormDescription>
              {isWinning &&
                Number(field.value) ===
                  Number(biddingDetailsRef.currentBidAmount) && (
                  <p className="mt-1 flex items-start gap-1 text-sm text-yellow-600 dark:text-yellow-500">
                    <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                      Warning: Setting max bid equal to your current bid will
                      disable proxy bidding. You won&apos;t automatically bid
                      higher if outbid.
                    </span>
                  </p>
                )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting
              ? "Submitting..."
              : isWinning
                ? "Update max bid"
                : "Place a bid"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
